import React, { useRef, useEffect, useState } from "react";
import { Pencil, Type, Eraser } from "lucide-react";

/**
 * Dual-mode signature capture:
 *   - "draw" — HTML canvas pad (pointer events, works on touch + mouse)
 *   - "type" — typed name rendered in a script-y font, baked to canvas on submit
 *
 * onChange({ name, dataurl }) fires whenever either mode produces output.
 */
export function SignaturePad({ defaultName = "", onChange }) {
  const [mode, setMode] = useState("draw");
  const [name, setName] = useState(defaultName);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);

  // Resize canvas to its container width.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const fit = () => {
      const w = c.parentElement.clientWidth;
      const dpr = window.devicePixelRatio || 1;
      c.style.width = w + "px";
      c.style.height = "160px";
      c.width = w * dpr;
      c.height = 160 * dpr;
      const ctx = c.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#111";
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [mode]);

  const emit = () => {
    if (mode === "draw") {
      const c = canvasRef.current;
      if (!c) return;
      const dataurl = hasInkRef.current ? c.toDataURL("image/png") : null;
      onChange && onChange({ name: name.trim(), dataurl });
    } else {
      // Typed signature → render onto an offscreen canvas (italic script).
      const off = document.createElement("canvas");
      off.width = 600;
      off.height = 160;
      const ctx = off.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, 600, 160);
      ctx.fillStyle = "#111";
      ctx.font = 'italic 56px "Brush Script MT", "Snell Roundhand", "Lucida Handwriting", cursive';
      ctx.textBaseline = "middle";
      ctx.fillText(name.trim() || " ", 20, 80);
      const dataurl = name.trim() ? off.toDataURL("image/png") : null;
      onChange && onChange({ name: name.trim(), dataurl });
    }
  };

  const start = (e) => {
    const c = canvasRef.current;
    if (!c) return;
    drawingRef.current = true;
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    const ctx = c.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    const ctx = c.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInkRef.current = true;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    emit();
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    hasInkRef.current = false;
    emit();
  };

  return (
    <div data-testid="signature-pad" style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "#fff" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button type="button"
          className={`btn sm ${mode === "draw" ? "gold" : "ghost"}`}
          onClick={() => setMode("draw")}
          data-testid="sig-mode-draw">
          <Pencil size={13} /> Draw
        </button>
        <button type="button"
          className={`btn sm ${mode === "type" ? "gold" : "ghost"}`}
          onClick={() => setMode("type")}
          data-testid="sig-mode-type">
          <Type size={13} /> Type
        </button>
        {mode === "draw" && (
          <button type="button" className="btn sm ghost" onClick={clearCanvas} style={{ marginLeft: "auto" }} data-testid="sig-clear">
            <Eraser size={13} /> Clear
          </button>
        )}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <label style={{ fontSize: 12, color: "var(--text-2)" }}>Name and Surname <span className="req">*</span></label>
          <input
            className="input"
            value={name}
            placeholder="Type your full name"
            onChange={(e) => { setName(e.target.value); }}
            onBlur={emit}
            data-testid="sig-name"
          />
        </div>

        {mode === "draw" ? (
          <div style={{ background: "#fff", border: "1px dashed var(--border)", borderRadius: 6, position: "relative" }}>
            <canvas
              ref={canvasRef}
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={end}
              onPointerLeave={end}
              style={{ display: "block", touchAction: "none", cursor: "crosshair" }}
              data-testid="sig-canvas"
            />
            {!hasInkRef.current && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", color: "#bbb", fontSize: 13 }}>
                Draw your signature here
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: "#fff", border: "1px dashed var(--border)", borderRadius: 6, padding: "20px 16px", minHeight: 120, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
            <span style={{ fontFamily: '"Brush Script MT","Snell Roundhand","Lucida Handwriting",cursive', fontStyle: "italic", fontSize: 44, color: "#111" }} data-testid="sig-typed-preview">
              {name.trim() || <span style={{ color: "#bbb", fontSize: 14, fontFamily: "inherit", fontStyle: "normal" }}>Your typed name will appear as a signature here</span>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
